import React, { Component } from "react";
import { Segment, Table, Input, Grid, Button, Icon } from "semantic-ui-react";

import sortBy from "lodash/sortBy";
import find from "lodash/find";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import titleCase from "title-case";
import { resolveGradingScheme } from "../../utils/gradingScheme";

const _ = {
  sortBy,
  find,
  groupBy,
  isEmpty,
  orderBy,
};

const changeCase = {
  titleCase: titleCase,
};

class SectionStudents extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: [],
      searchKey: "",
      selectedIndex: 0,
      column: null,
      direction: null,
      studentName: "",
      id: "",
      sectionId: "",
      filtered: false,
      currentQuarter: 1,
    };
    this.onSearchChange = this.onSearchChange.bind(this);
    this.handlePaginationChangeMale =
      this.handlePaginationChangeMale.bind(this);
    this.handlePaginationChangeFemale =
      this.handlePaginationChangeFemale.bind(this);
    this.handleSort = this.handleSort.bind(this);
  }

  filterTopTen = () => {
    this.setState({ filtered: !this.state.filtered });
  };

  isSearched(searchKey) {
    return function (item) {
      return (
        !searchKey ||
        item.student.firstName
          .toLowerCase()
          .includes(searchKey.toLowerCase()) ||
        item.student.lastName.toLowerCase().includes(searchKey.toLowerCase())
      );
    };
  }

  onSearchChange(event) {
    this.setState({ searchKey: event.target.value });
  }

  handleSort(clickedColumn) {
    const { column, data, direction } = this.state;
    if (column !== clickedColumn) {
      if (clickedColumn === "name") {
        this.setState({
          column: clickedColumn,
          data: _.sortBy(data, function (o) {
            return o.student.lastName;
          }),
          direction: "ascending",
        });
        return;
      } else {
        this.setState({
          column: clickedColumn,
          data: _.sortBy(data, [clickedColumn]),
          direction: "ascending",
        });
        return;
      }
    }
    this.setState({
      data: data.reverse(),
      direction: direction === "ascending" ? "descending" : "ascending",
    });
  }

  handlePaginationChangeMale(e, { activePage }) {
    this.setState({
      activePageMale: activePage,
    });
  }

  handlePaginationChangeFemale(e, { activePage }) {
    this.setState({
      activePageFemale: activePage,
    });
  }

  componentDidMount() {
    // This used to re-derive the quarter from the month with its own boundaries,
    // which disagreed with the rest of the app and left November, July and
    // August unassigned - so currentQuarter came out undefined and the Top Ten
    // silently went unsorted. Take it from the caller, defaulting to period 1.
    this.setState({ data: this.props.sectionStudents });
  }

  currentPeriodIndex(periods) {
    const fromProps = parseInt(this.props.currentQuarter, 10);
    if (periods.some((p) => p.index === fromProps)) return fromProps;
    return periods[0].index;
  }

  componentDidUpdate(prevProps) {
    // Only update if sectionStudents actually changed
    if (prevProps.sectionStudents !== this.props.sectionStudents) {
      this.setState({ data: this.props.sectionStudents });
    }
  }

  render() {
    const {
      column,
      direction,
      data,
      searchKey,
      filtered,
    } = this.state;
    if (_.isEmpty(data)) return null;

    const gender = _.groupBy(data, (o) => o.student.gender);
    let currentDataMale = [],
      currentDataFemale = [];
    if (gender.hasOwnProperty("Male")) {
      currentDataMale = gender["Male"].filter(this.isSearched(searchKey));
    }

    if (gender.hasOwnProperty("Female")) {
      currentDataFemale = gender["Female"].filter(this.isSearched(searchKey));
    }
    let studentAverage = [];

    // Helper to display empty string for 0 values
    const displayValue = (val) => (val === 0 || val === '0' || val === '0.00') ? '' : val;

    // Averages are per school year, so the column count follows the calendar.
    const scheme = resolveGradingScheme(
      this.props.schoolYear,
      this.props.section ? this.props.section.gradeLevel : null,
      { levelType: this.props.section ? this.props.section.levelType : '' }
    );
    const periods = scheme.periods;

    // A descriptive section has letters, not numbers. There is nothing to
    // average and DepEd confers no Key Stage 1 honors, so this whole view -
    // averages and Top Ten alike - does not apply.
    const showsAverages = scheme.usesGeneralAverage;

    // One row builder, used by both tables. They were near-duplicates that had
    // already drifted: the female block zeroed all four averages when a student
    // had no conduct record and the male block did not.
    const buildRow = (sectionStudent, key) => {
      const studentName =
        sectionStudent.student.lastName + ", " + sectionStudent.student.firstName;

      const classConduct =
        sectionStudent.classConducts && sectionStudent.classConducts.length !== 0
          ? sectionStudent.classConducts[0]
          : null;

      const averages = periods.map((period) => {
        if (!sectionStudent.grades || sectionStudent.grades.length === 0) return 0;

        const total = sectionStudent.grades.reduce(
          (sum, g) => sum + (Number(g[period.dbField]) || 0),
          0
        );
        let average = Math.round(total / sectionStudent.grades.length);

        // The same 90/10 subject-to-conduct blend the report card uses.
        if (classConduct) {
          average = parseFloat(
            average * 0.9 + (Number(classConduct[period.conductField]) || 0) * 0.1
          ).toFixed(2);
        }
        return parseFloat(average);
      });

      studentAverage.push({
        firstName: sectionStudent.student.firstName,
        lastName: sectionStudent.student.lastName,
        sectionId: sectionStudent.sectionId,
        sectionStudentId: sectionStudent.sectionStudentId,
        averages: averages,
      });

      return (
        <Table.Row
          key={key}
          className="sections-table-row"
          onClick={() =>
            this.props.handleRowClick(
              sectionStudent.sectionId,
              sectionStudent.sectionStudentId
            )
          }
        >
          <Table.Cell>{studentName}</Table.Cell>
          {averages.map((value, i) => (
            <Table.Cell key={periods[i].index} collapsing textAlign="right">
              {displayValue(value)}
            </Table.Cell>
          ))}
        </Table.Row>
      );
    };

    const studentsTableMale = currentDataMale.map(buildRow);
    const studentsTableFemale = currentDataFemale.map(buildRow);

    // Rank on the period being viewed. The old code had no branch for a final
    // average and simply logged a TODO, so any unmatched quarter left the list
    // in whatever order it arrived.
    const rankIndex = periods.findIndex(
      (p) => p.index === this.currentPeriodIndex(periods)
    );
    studentAverage = _.orderBy(
      studentAverage,
      [(student) => student.averages[rankIndex]],
      ["desc"]
    );
    studentAverage = studentAverage.splice(0, 10);
    const topTenTable = studentAverage.map((student, key) => {
      const studentName = student.lastName + ", " + student.firstName;
      return (
        <Table.Row
          key={key}
          className="sections-table-row"
          onClick={() =>
            this.props.handleRowClick(
              student.sectionId,
              student.sectionStudentId
            )
          }
        >
          <Table.Cell>{studentName}</Table.Cell>
          {student.averages.map((value, i) => (
            <Table.Cell key={periods[i].index} collapsing textAlign="right">
              {displayValue(value)}
            </Table.Cell>
          ))}
        </Table.Row>
      );
    });

    // Column headings come from the school year rather than fixed state, so a
    // three-term year shows T1/T2/T3 instead of 1st through 4th.
    const columnLabels = ["name"].concat(periods.map((p) => p.shortLabel));

    const headers = columnLabels.map((field, index) => {
      return (
        <Table.HeaderCell
          key={index}
          sorted={column === field ? direction : null}
          onClick={() => this.handleSort(field)}
          textAlign="center"
        >
          {changeCase.titleCase(field)}
        </Table.HeaderCell>
      );
    });

    return (
      <div className="table-sections">
        <Grid>
          <Grid.Column floated="left" width={14}>
            <Input
              icon="search"
              fluid
              value={searchKey}
              onChange={this.onSearchChange}
              className="section-students-search"
              placeholder="Search Students"
            />
          </Grid.Column>
          <Grid.Column floated="right" width={2}>
            {showsAverages && (
              <Button
                icon
                floated="right"
                color="yellow"
                onClick={() => this.filterTopTen()}
              >
                <Icon name="star" />
              </Button>
            )}
          </Grid.Column>
        </Grid>
        {data.length > 0 ? (
          <Grid centered>
            {filtered ? (
              <Grid.Column width={8}>
                <Table celled selectable sortable striped compact size="small">
                  <Table.Header>
                    <Table.Row>{headers}</Table.Row>
                  </Table.Header>

                  <Table.Body>{topTenTable}</Table.Body>
                </Table>
              </Grid.Column>
            ) : (
              <React.Fragment>
                <Grid.Column width={8}>
                  <h4>Male</h4>
                  <Table
                    celled
                    selectable
                    sortable
                    striped
                    compact
                    size="small"
                  >
                    <Table.Header>
                      <Table.Row>{headers}</Table.Row>
                    </Table.Header>

                    <Table.Body>{studentsTableMale}</Table.Body>
                  </Table>
                </Grid.Column>
                <Grid.Column width={8}>
                  <h4>Female</h4>
                  <Table
                    celled
                    selectable
                    sortable
                    striped
                    compact
                    size="small"
                  >
                    <Table.Header>
                      <Table.Row>{headers}</Table.Row>
                    </Table.Header>

                    <Table.Body>{studentsTableFemale}</Table.Body>
                  </Table>
                </Grid.Column>
              </React.Fragment>
            )}
          </Grid>
        ) : (
          <Segment>No student records found.</Segment>
        )}
      </div>
    );
  }
}

export default SectionStudents;
